export interface UrlMetadata {
    image?: string;
    favicon?: string;
}

// Google's favicon service returns a generic globe icon (served from these hosts)
// when it has no real favicon for the domain.
export const GOOGLE_FAVICON_HOST_RE = /gstatic\.com|google\.com\/s2\/favicons/;

export interface UrlMetadataMap {
    [url: string]: UrlMetadata;
}

// Helper to validate if a string is a valid website URL
export const isValidWebsiteUrl = (url: string): boolean => {
    if (!url || typeof url !== 'string') return false;

    const trimmedUrl = url.trim();

    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
        try {
            new URL(trimmedUrl);
            return true;
        } catch {
            return false;
        }
    }

    return false;
};

// Helper to normalize URLs by ensuring they have a protocol
export const normalizeUrl = (url: string): string => {
    const trimmed = url?.trim();
    if (!trimmed) return '';

    // If the URL doesn't start with http or https, prepend https://
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Helper to get domain favicon
export const getFaviconUrl = (websiteUrl: string): string => {
    if (!websiteUrl?.trim()) {
        return '';
    }
    try {
        // Ensure URL has a protocol
        const normalizedUrl = normalizeUrl(websiteUrl);
        const url = new URL(normalizedUrl);
        return `${url.protocol}//${url.hostname}/favicon.ico`;
    } catch (error) {
        console.warn(`Failed to parse URL for favicon: ${websiteUrl}`, error);
        return '';
    }
};

// Helper to get company logo from clearbit API (free, no authentication needed)
export const getCompanyLogo = (websiteUrl: string): string => {
    if (!websiteUrl?.trim()) {
        return '';
    }
    try {
        // Ensure URL has a protocol
        const normalizedUrl = normalizeUrl(websiteUrl);
        const url = new URL(normalizedUrl);
        return `https://logo.clearbit.com/${url.hostname}`;
    } catch (error) {
        console.warn(`Failed to generate company logo URL: ${websiteUrl}`, error);
        return '';
    }
};

/* 
   Helper to get a company icon from Google's favicon service. Normalizes the
   protocol (so bare domains like "tatamotors.com" work) and resolves for far
   more domains than Clearbit, which has been largely sunset.
*/
export const getGoogleFaviconUrl = (websiteUrl: string, size = 64): string => {
    if (!websiteUrl?.trim()) {
        return '';
    }
    try {
        const url = new URL(normalizeUrl(websiteUrl));
        return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=${size}`;
    } catch (error) {
        console.warn(`Failed to generate google favicon URL: ${websiteUrl}`, error);
        return '';
    }
};

/*
   Builds the ordered logo-source chain shared by the CompanyLogoBadge, matching
   what KeyCompetitors already uses so logos are consistent app-wide:
     stored logo (if the surface has one) → site favicon → Google favicon.
   The consumer renders these in turn (advancing on <img> error) before dropping
   to a first-letter badge. No protocol gate — the helpers normalize a bare
   domain (e.g. "stripe.com") and return '' if unusable.

   Note: Clearbit (logo.clearbit.com) and apple-touch-icon were tried but drop:
   Clearbit's host no longer resolves (service sunset) and cross-origin
   apple-touch-icon fetches are blocked by the browser (ORB), so both only
   produced failed requests before falling through to these favicons anyway.
*/
// Sentinels the backend sends for a missing website — must NOT be treated as a
// real domain, otherwise we build junk favicons like https://na/favicon.ico.
const WEBSITE_SENTINELS = new Set(['', 'na', 'n/a', 'nil', 'none', 'null', 'undefined', '-']);

// True only for a value that looks like an actual website (not a sentinel).
export const isUsableWebsite = (websiteUrl?: string): boolean => {
    const v = (websiteUrl ?? '').trim().toLowerCase();
    return !!v && !WEBSITE_SENTINELS.has(v);
};

// Returns the first candidate that is a usable website, skipping empty/"NA"/etc.
// Use this instead of `a || b` so a sentinel like "NA" doesn't win over a real URL.
export const pickWebsite = (...candidates: (string | undefined)[]): string =>
    candidates.map((c) => (c ?? '').trim()).find((c) => isUsableWebsite(c)) ?? '';

// The AI overview lists the company itself as a competitor (the "Target company"
// entry). Returns that entry's raw website (undefined if none). Matches by the
// description tag, then a name match against the company's own name, then falls
// back to the first competitor (the report lists the subject first).
export const findSubjectCompanyWebsite = (competitors: any[] | undefined, orgName?: string): string | undefined => {
    const list = competitors ?? [];
    const orgNameNorm = (orgName ?? '').trim().toLowerCase();
    const subject =
        list.find(
            (c: any) =>
                /(target|subject)\s+company/i.test(c?.description ?? '') ||
                (!!orgNameNorm && (c?.companyName ?? '').trim().toLowerCase().includes(orgNameNorm)),
        ) ?? list[0];
    return subject?.website;
};

export const buildLogoSources = (logo?: string, websiteUrl?: string): string[] => {
    const sources: string[] = [];
    if (logo?.trim()) sources.push(logo.trim());
    const site = websiteUrl?.trim();
    if (site && isUsableWebsite(site)) {
        const favicon = getFaviconUrl(site);
        if (favicon) sources.push(favicon);
        const google = getGoogleFaviconUrl(site, 128);
        if (google) sources.push(google);
    }
    return sources;
};
